import { Box, Grid2, IconButton, Typography } from "@mui/material";
import * as React from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ApexCharts from 'apexcharts';

import { setCurrentDatesForChart } from "../../../Actions/DigitalTwinActions";
import { allocateColors, getBatterySignalsData, getDigitalTwinChartData, getLeveledMetrics, prepareContinuousSignalsData } from "../../../Helper/DigitalTwin/DigitalTwinFunctions";
import { IDDisplayBox } from "../../../Theme/DigitalTwinStyles";
import { usePagesContent, useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import { toTitleCase } from "../../../Helper/Formatters/TextFormatter";
import { allMetrics } from "../../../Helper/DigitalTwin/DataFile";
import VitalsSelectionDropdown from "../../DigitalTwinV3/components/VitalsSelectionDropdown";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { PrimaryTooltip, StatusDot } from "../../../Components/StyledComponent";
import { changeDateFormat, getDatePickerDate, getLocalDateFromUTC } from "../../../Helper/DatePicker/DateConverter";
import SingleAlertChart from "./SingleAlertChart";
import SingleAlertDateCarousel from "./SingleAlertDateCarousel";
import { useNavigate } from "react-router-dom";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import CustomIcon from "../../../Components/CustomIcon";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { calculateIntervalFromTimeWindow, formatChartTimestampForApi } from "../../../Helper/Charts/ChartHelper";

dayjs.extend(utc);
dayjs.extend(timezone);


export default function SingleAlertChartSection({
    incidentInfo,
    battery,
    alertIncident,
    isPlatformAlert = false
}) {
    const { baseEntityType, baseEntityTypeIDLable } = useBaseEntityInfo();
    const selectedNode = {
        id: alertIncident.entityAffectedID,
        level: alertIncident.entityAffected,
        displayId: alertIncident.entityAffectedDisplayID,
    }

    const pageContent = usePagesContent()
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const colorMemoryRef = React.useRef(new Map());

    const { t } = useTranslation(['common', 'digital_twin'])

    const localActivatedAt = getLocalDateFromUTC(alertIncident.customerDBTimeInsert, 'YYYY-MM-DD HH:mm:ss')
    const localResolvedAt = alertIncident.resolveCustomerDBTime ? getLocalDateFromUTC(alertIncident.resolveCustomerDBTime, 'YYYY-MM-DD HH:mm:ss') : undefined
    const localExpiredAt = alertIncident.expiredAt ? getLocalDateFromUTC(alertIncident.expiredAt, 'YYYY-MM-DD HH:mm:ss') : undefined

    const [currentAlert, setCurrentAlert] = React.useState({
        activatedAt: localActivatedAt,
        resolvedAt: localResolvedAt,
        expiredAt: localExpiredAt,
        alertStatus: alertIncident.bmsAlertStatus,
        activatedAtDate: changeDateFormat(localActivatedAt, 'YYYY-MM-DD'),
        resolvedAtDate: changeDateFormat(localResolvedAt, 'YYYY-MM-DD'),
        expiredAtDate: changeDateFormat(localExpiredAt, 'YYYY-MM-DD')
    })

    const [currentDates, setCurrentDates] = React.useState({
        startDate: getDatePickerDate(currentAlert.activatedAtDate, -1),
        endDate: currentAlert.expiredAtDate || currentAlert.resolvedAtDate || getDatePickerDate(undefined, 0),
    })

    const [primaryGranularity, setPrimaryGranularity] = React.useState('raw')
    const [secondaryGranularity, setSecondaryGranularity] = React.useState('avg')
    const [selectedMetrics, setSelectedMetrics] = React.useState([])
    const [nodeMetrics, setNodeMetrics] = React.useState(allMetrics)
    const [series, setSeries] = React.useState([])
    const [selectedDate, setSelectedDate] = React.useState({ key: 0, value: currentDates.endDate })
    const [isChartLoading, setIsChartLoading] = React.useState(true)
    const [categories, setCategories] = React.useState([])
    const [visibleSeries, setVisibleSeries] = React.useState(series.map(() => true));
    const [intervalSeconds, setIntervalSeconds] = React.useState(300);
    const [isZoomed, setIsZoomed] = React.useState(false);
    const [zoomedRange, setZoomedRange] = React.useState(null);
    const zoomTimeoutRef = React.useRef(null);


    const chartID = 'bms-faults-incident-chart';

    const handleDateChange = ({ startDate, endDate }) => {
        dispatch(setCurrentDatesForChart({ startDate, endDate }))
        setCurrentDates({ startDate, endDate })
    }

    const fetchChartData = async (overrideIntervalSeconds = null) => {
        if (!selectedNode || !selectedNode.id || !selectedMetrics || selectedMetrics.length < 1) {
            setIsChartLoading(false)
            return false
        }

        setIsChartLoading(true)

        const intervalToUse = overrideIntervalSeconds !== null ? overrideIntervalSeconds : intervalSeconds;

        try {
            const [seriesData, chartData, categories] = await getDigitalTwinChartData({
                batteryID: battery.batteryID,
                startDate: currentDates.startDate,
                endDate: currentDates.endDate,
                selectedDate: selectedDate.value,
                primaryGranularity: primaryGranularity,
                secondaryGranularity: secondaryGranularity,
                level: selectedNode.level,
                selectedNode: selectedNode,
                metricKeys: selectedMetrics.map(metric => metric.key),
                selectedMetrics: selectedMetrics,
                intervalSeconds: intervalToUse
            })

            setIsChartLoading(false)

            setCategories(categories)
            setVisibleSeries(seriesData.map(() => true))
            const { coloredSignals, nextAssignment } = allocateColors({
                signals: seriesData,
                prevAssignment: colorMemoryRef.current,
                getKey: s => s.key,
            });
            colorMemoryRef.current = nextAssignment;
            setSeries(coloredSignals);
        } catch (err) {
            setIsChartLoading(false)
        }
    }

    const resetChartData = async () => {
        await fetchChartData(300);
    }

    const changeSelectedDateFromList = (date) => {
        // Reset zoom state when date changes
        setIsZoomed(false);
        setZoomedRange(null);
        setIntervalSeconds(300);
        setSelectedDate({ key: selectedDate.key + 1, value: date })
    }

    const resetChartDataAfterZoom = React.useCallback(async (xaxis) => {
        // Clear any existing timeout
        if (zoomTimeoutRef.current) {
            clearTimeout(zoomTimeoutRef.current);
        }

        // Debounce the zoom update to prevent multiple rapid calls
        zoomTimeoutRef.current = setTimeout(async () => {
            setIsChartLoading(true);

            // Use the xaxis parameter from beforeZoom event
            const minX = xaxis?.min;
            const maxX = xaxis?.max;

            if (!minX || !maxX) {
                setIsChartLoading(false);
                return;
            }

            // Calculate the appropriate interval based on the visible time window
            const calculatedInterval = calculateIntervalFromTimeWindow(minX, maxX);

            // Convert minX and maxX timestamps to date strings for the API
            const zoomedStartDate = formatChartTimestampForApi(minX);
            const zoomedEndDate = formatChartTimestampForApi(maxX);

            // Store zoom state
            setIsZoomed(true);
            setZoomedRange({ minX, maxX });
            setIntervalSeconds(calculatedInterval);

            // Fetch new data with the zoomed range
            if (!selectedNode || !selectedNode.id || !selectedMetrics || selectedMetrics.length < 1) {
                setIsChartLoading(false);
                return;
            }

            try {
                const [seriesData, chartData, categories] = await getDigitalTwinChartData({
                    batteryID: battery.batteryID,
                    startDate: zoomedStartDate,
                    endDate: zoomedEndDate,
                    selectedDate: zoomedStartDate,
                    primaryGranularity: primaryGranularity,
                    secondaryGranularity: secondaryGranularity,
                    level: selectedNode.level,
                    selectedNode: selectedNode,
                    metricKeys: selectedMetrics.map(metric => metric.key),
                    selectedMetrics: selectedMetrics,
                    intervalSeconds: calculatedInterval
                })

                setIsChartLoading(false);

                setCategories(categories);
                setVisibleSeries(seriesData.map(() => true));
                const { coloredSignals, nextAssignment } = allocateColors({
                    signals: seriesData,
                    prevAssignment: colorMemoryRef.current,
                    getKey: s => s.key,
                });
                colorMemoryRef.current = nextAssignment;
                setSeries(coloredSignals);
            } catch (err) {
                setIsChartLoading(false);
            }
        }, 500); // 500ms debounce
    }, [selectedNode, selectedMetrics, battery.batteryID, primaryGranularity, secondaryGranularity]);

    const handleZoomReset = React.useCallback(async () => {
        // Reset interval to default (300 seconds / 5 minutes)
        const defaultInterval = 300;
        setIntervalSeconds(defaultInterval);

        // Reset zoom state
        setIsZoomed(false);
        setZoomedRange(null);

        // Refetch chart data with original date range and default interval
        await fetchChartData(defaultInterval);
    }, [fetchChartData]);

    const resetChartToLatestData = () => {
        if (!incidentInfo.lastPingedTime) {
            alert("No data available for selected date")
        }

        let lastPingDate = getLocalDateFromUTC(incidentInfo.lastPingedTime)

        let startDateNew = getDatePickerDate(lastPingDate, 0)
        let endDateNew = getDatePickerDate(lastPingDate, 0)

        handleDateChange({ startDate: startDateNew, endDate: endDateNew })
    }

    const toggleSeriesOnLegendClick = (index) => {
        const updatedVisibility = [...visibleSeries];
        updatedVisibility[index] = !updatedVisibility[index];
        setVisibleSeries(updatedVisibility);
        const seriesName = series[index].name;

        if (updatedVisibility[index]) {
            ApexCharts.exec(chartID, 'showSeries', seriesName);
        } else {
            ApexCharts.exec(chartID, 'hideSeries', seriesName);
        }


        if (secondaryGranularity !== 'avg') {
            if (updatedVisibility[index]) {
                ApexCharts.exec(chartID, 'showSeries', `${seriesName} mix`);
            } else {
                ApexCharts.exec(chartID, 'hideSeries', `${seriesName} mix`);
            }
        }
    };

    const setupInitialMetrics = async () => {
        if (!selectedNode.level) {
            return null
        }

        let nodeLeveledMetrics = getLeveledMetrics(selectedNode.level, pageContent, t)
        setSelectedMetrics(nodeLeveledMetrics.slice(0, 4));
        setNodeMetrics(nodeLeveledMetrics);

        const { continuousSignals } = await getBatterySignalsData(battery.batteryID, selectedNode.level);
        const { formattedContinuousSignals, defaultSelectedContinuousSignals } = prepareContinuousSignalsData(continuousSignals, selectedNode?.level);

        const { coloredSignals, nextAssignment } = allocateColors({
            signals: defaultSelectedContinuousSignals,
            prevAssignment: colorMemoryRef.current,
            getKey: s => s.key,
        });
        colorMemoryRef.current = nextAssignment;

        setSelectedMetrics(coloredSignals);
        setNodeMetrics(formattedContinuousSignals);
    };

    const applySelectedMetrics = (items) => {
        const { coloredSignals, nextAssignment } = allocateColors({
            signals: items,
            prevAssignment: colorMemoryRef.current,
            getKey: s => s.key,
        });
        colorMemoryRef.current = nextAssignment;
        setSelectedMetrics(coloredSignals)
    }

    const getSelectedDate = () => {
        return currentAlert.expiredAtDate || currentAlert.resolvedAtDate || currentAlert.activatedAtDate
    }

    const redirectToBatteryVitals = () => {
        let temp = {
            batteryID: battery.batteryID,
            batterySearchedGlobal: "false",
            navigateFromTab: true,
            tabValue: 'batteryVitals',
            startDate: getSelectedDate(),
            endDate: currentDates.endDate,
            selecetedNodeId: selectedNode.id,
        }
        encryptTheParams(temp, navigate, true);
    }

    React.useEffect(() => {
        setupInitialMetrics()
    }, [alertIncident])


    React.useEffect(() => {
        resetChartData();
    }, [selectedDate, selectedMetrics]);


    if (!selectedNode || !selectedNode.id) {
        return <CartLoader />;
    }

    const metricNames = selectedMetrics.map((metric) => metric.name)

    return (
        <Grid2 sx={{ padding: '0 32px 32px 32px', width: '100%' }}>
            <Grid2 container justifyContent={'space-between'} sx={{ mb: 1.5 }}>
                <Grid2 container gap={'8px'}>
                    <Grid2 container alignItems={'center'}>
                        <Typography variant="fw500s14c000000">Battery Vitals Behaviour </Typography>
                    </Grid2>
                </Grid2>

                <Grid2 container gap={1} alignItems={'center'} paddingRight={1}>
                    <IDDisplayBox>
                        <StatusDot
                            sx={{ marginRight: '0px' }}
                            color={battery?.pingStatus?.toLowerCase() === 'active' ? '#09B820' : '#8F8F8F'}
                        />
                        <Box>
                            {alertIncident?.entityAffected?.toLowerCase() === 'battery' ? (
                                // Battery-level alert: use org-meta label (Bus Number / Battery ID)
                                <>
                                    <Typography variant="fw400s12c494949">{baseEntityTypeIDLable}: </Typography>
                                    <Typography variant="fw400s12c494949">
                                        {baseEntityType.toLowerCase() === 'vehicle' ? battery?.licensePlateNo : battery?.deviceID}
                                    </Typography>
                                </>
                            ) : (
                                // Sub-battery alert (string, module, cell, TMS): show specific entity
                                <>
                                    <Typography variant="fw400s12c494949">
                                        {toTitleCase(t(`digital_twin:${alertIncident?.entityAffected}`, alertIncident?.entityAffected))} ID:{' '}
                                    </Typography>
                                    <Typography variant="fw400s12c494949">{alertIncident?.entityAffectedDisplayID}</Typography>
                                </>
                            )}
                        </Box>
                    </IDDisplayBox>
                    <PrimaryTooltip title="Go to Battery Vitals" position="bottom">
                        <IconButton onClick={() => redirectToBatteryVitals()} sx={{ padding: '7px' }}>
                            <CustomIcon name='redirect-icon' sx={{ color: "#6D72F6", fontSize: "18px" }} />
                        </IconButton>
                    </PrimaryTooltip>
                </Grid2>
            </Grid2>

            <Grid2 container alignItems={'center'} gap={'12px'} sx={{ mt: 1 }}>
                <VitalsSelectionDropdown
                    itemList={nodeMetrics}
                    selectedItems={selectedMetrics}
                    applySelectedItems={applySelectedMetrics}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {series.filter((s, i) => metricNames.includes(s.name)).map((s, i) => (
                        <Box
                            key={i}
                            onClick={() => toggleSeriesOnLegendClick(i)}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                opacity: visibleSeries[i] ? 1 : 0.5
                            }}>
                            <StatusDot sx={{ marginRight: '8px' }} color={s.color} />
                            <Typography variant="fw400s12c3D4B5A">{s.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Grid2 >

            <Box sx={{ mt: 1.5 }}>
                <SingleAlertChart
                    chartID={chartID}
                    isPlatformAlert={isPlatformAlert}
                    metrics={selectedMetrics}
                    currentAlert={currentAlert}
                    alertIncident={alertIncident}
                    incidentInfo={incidentInfo}
                    key={isChartLoading}
                    isLoading={isChartLoading}
                    series={series}
                    categories={categories}
                    startDate={selectedDate}
                    primaryGranularity={primaryGranularity}
                    resetToLatestDataDate={() => resetChartToLatestData()}
                    visibleSeries={visibleSeries}
                    onZoom={resetChartDataAfterZoom}
                    onZoomReset={handleZoomReset}
                    zoomedRange={zoomedRange}
                    setLoading={setIsChartLoading}
                    isZoomed={isZoomed}
                />
            </Box>

            <Box sx={{ opacity: isChartLoading ? 0 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: '50%' }}>
                    <SingleAlertDateCarousel
                        alertInfo={incidentInfo}
                        currentAlert={currentAlert}
                        primaryGranularity={primaryGranularity}
                        currentDates={currentDates}
                        onChangeDate={(date) => changeSelectedDateFromList(date)}
                    />
                </Box>
            </Box>
        </Grid2 >
    )
}
